import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { 
  Menu, 
  User, 
  LogOut, 
  LayoutDashboard, 
  Map, 
  Shield, 
  Settings, 
  Bell, 
  BarChart3,
  Satellite,
  Store,
  Smartphone,
  ChevronRight,
  Home,
  BookOpen,
  DollarSign,
  Info,
  X
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useUserRole } from '@/hooks/useUserRole';
import { NotificationBell } from '@/components/NotificationBell';
import { usePWA } from '@/hooks/usePWA';
import LanguageSelector from '@/components/LanguageSelector';
import { useTranslation } from 'react-i18next';
import logoMark from '@/assets/sentinel-crop-mark.png';

// Mobile header uses the icon-only mark for clarity at small sizes.
// The full logotype with text remains in footer, reports and PDFs.
const logoImage = logoMark;

const MobileNavigation = () => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const location = useLocation();
  const { isInsurer, isUrbanManager } = useUserRole();
  const { isInstalled } = usePWA();
  const { t } = useTranslation();

  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        
        if (user) {
          setUser(user);
          
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', user.id)
            .single();
            
          if (!profileError) {
            setProfile(profileData);
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };

    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Close sheet on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setOpen(false);
      navigate('/');
      toast({
        title: t('common.signedOut'),
        description: t('common.signedOutDesc'),
      });
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.signOutError'),
        variant: "destructive"
      });
    }
  };

  const isDashboardRoute = location.pathname.startsWith('/dashboard');
  const isActive = (path: string) => 
    location.pathname === path || 
    (path !== '/dashboard' && path !== '/' && location.pathname.startsWith(path));

  // Dashboard menu items
  const getDashboardMenuItems = () => {
    if (profile?.role === 'insurer') {
      return [
        { title: t('sidebar.insuranceDashboard'), url: '/dashboard/insurance-company', icon: Shield },
        { title: t('sidebar.analytics'), url: '/dashboard/analytics', icon: BarChart3 },
        { title: t('sidebar.satellite'), url: '/dashboard/satellite', icon: Satellite },
        { title: t('sidebar.alerts'), url: '/dashboard/alerts', icon: Bell },
        { title: t('sidebar.settings'), url: '/dashboard/settings', icon: Settings },
      ];
    }
    
    const baseItems = [
      { title: t('sidebar.overview'), url: '/dashboard', icon: LayoutDashboard },
      { title: isUrbanManager ? t('sidebar.greenSpaces') : t('sidebar.parcels'), url: '/dashboard/parcels', icon: Map },
      { title: t('sidebar.analytics'), url: '/dashboard/analytics', icon: BarChart3 },
      { title: t('sidebar.satellite'), url: '/dashboard/satellite', icon: Satellite },
      { title: t('sidebar.alerts'), url: '/dashboard/alerts', icon: Bell },
      { title: t('sidebar.settings'), url: '/dashboard/settings', icon: Settings },
    ];

    const farmerItems = !isUrbanManager ? [
      { title: t('sidebar.marketplace'), url: '/dashboard/marketplace', icon: Store },
    ] : [];

    return [...baseItems, ...farmerItems];
  };

  const landingNavItems = [
    { name: t('nav.pricing'), href: '/#pricing', icon: DollarSign },
    { name: t('nav.about'), href: '/about', icon: Info },
  ];

  const NavLink = ({ to, icon: Icon, label, active }: { to: string; icon: any; label: string; active: boolean }) => (
    <Link
      to={to}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
        active 
          ? 'bg-primary/10 text-primary' 
          : 'text-foreground/70 hover:bg-muted hover:text-foreground'
      }`}
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span className="flex-1">{label}</span>
      {active && <ChevronRight className="w-4 h-4 text-primary" />}
    </Link>
  );

  return (
    <nav className="bg-background/95 backdrop-blur-md border-b border-border sticky top-0 z-50">
      <div className="px-4">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center transition-opacity hover:opacity-80" aria-label="Sentinel Crop">
            <img
              src={logoImage}
              alt="Sentinel Crop"
              className="h-11 w-11 object-contain"
            />
          </Link>

          <div className="flex items-center gap-1">
            <LanguageSelector />
            {user && <NotificationBell />}
            
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] p-0 bg-background">
                <SheetHeader className="px-4 pt-4 pb-2">
                  <div className="flex items-center justify-between">
                    <SheetTitle className="flex items-center gap-2">
                      <img src={logoImage} alt="Sentinel Crop" className="h-8 w-8 object-contain" />
                      <span className="font-heading text-xs font-medium uppercase tracking-[0.16em]">Sentinel Crop</span>
                    </SheetTitle>
                  </div>
                </SheetHeader>

                <div className="flex flex-col h-[calc(100%-60px)] overflow-y-auto">
                  {/* User Info */}
                  {user && (
                    <div className="px-4 py-3 mx-3 mt-2 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {profile?.first_name || user.email?.split('@')[0]}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Dashboard Navigation */}
                  {user && isDashboardRoute && (
                    <div className="px-3 pt-4">
                      <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        {t('nav.dashboard')}
                      </p>
                      <div className="space-y-0.5">
                        {getDashboardMenuItems().map((item) => (
                          <NavLink
                            key={item.url}
                            to={item.url}
                            icon={item.icon}
                            label={item.title}
                            active={isActive(item.url)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Landing Navigation */}
                  {!isDashboardRoute && (
                    <div className="px-3 pt-4">
                      <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        {t('nav.features')}
                      </p>
                      <div className="space-y-0.5">
                        <NavLink to="/" icon={Home} label="Home" active={location.pathname === '/'} />
                        {landingNavItems.map((item) => (
                          <NavLink
                            key={item.href}
                            to={item.href}
                            icon={item.icon}
                            label={item.name}
                            active={isActive(item.href)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Install App */}
                  {!isInstalled && (
                    <>
                      <Separator className="my-3 mx-3" />
                      <div className="px-3">
                        <NavLink 
                          to="/install" 
                          icon={Smartphone} 
                          label={`📲 ${t('nav.installApp')}`} 
                          active={isActive('/install')}
                        />
                      </div>
                    </>
                  )}

                  <div className="flex-1" />

                  {/* Auth Section */}
                  <div className="px-3 pb-6 mt-4">
                    <Separator className="mb-4" />
                    {loading ? (
                      <div className="px-4 py-3">
                        <div className="w-24 h-6 bg-muted animate-pulse rounded" />
                      </div>
                    ) : user ? (
                      <div className="space-y-1">
                        {!isDashboardRoute && (
                          <NavLink
                            to="/dashboard"
                            icon={LayoutDashboard}
                            label={t('nav.dashboard')}
                            active={false}
                          />
                        )}
                        <button 
                          onClick={handleSignOut}
                          className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium w-full text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <LogOut className="w-5 h-5" />
                          {t('nav.signOut')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2 px-1">
                        <Button
                          variant="outline"
                          className="w-full justify-center"
                          onClick={() => { setOpen(false); navigate('/login'); }}
                        >
                          {t('nav.signIn')}
                        </Button>
                        <Button
                          className="w-full justify-center"
                          onClick={() => { setOpen(false); navigate('/register'); }}
                        >
                          {t('nav.getStarted')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default MobileNavigation;
